'use client';

import React from 'react';
import type { Airstrip, Leg, ResolvedExtraService } from '@/types';
import type { Advisory } from '../../services/advisoryRules';
import QuoteLegCard from './QuoteLegCard';
import { ArrowDown, MoonStar, Plus, RotateCcw } from 'lucide-react';
import SectionAdvisories from './SectionAdvisories';

type QuoteLegsProps = {
  legs: Leg[];
  airstrips: Airstrip[];
  weatherByCode: Record<string, { condition?: string; wind?: string; temp?: number; description?: string } | null>;
  issueDate: string;
  advisories?: Advisory[];
  sleepOverChargeByLeg?: Record<string, number>;
  onAddLeg: () => void;
  onAutoReturn: () => void;
  onRemoveLeg: (id: string) => void;
  onUpdateLeg: (id: string, updates: Partial<Leg>) => void;
  resolvedExtraServicesByLeg: Record<string, ResolvedExtraService[]>;
};

const QuoteLegs: React.FC<QuoteLegsProps> = ({
  legs,
  airstrips,
  weatherByCode,
  issueDate,
  advisories = [],
  sleepOverChargeByLeg = {},
  onAddLeg,
  onAutoReturn,
  onRemoveLeg,
  onUpdateLeg,
  resolvedExtraServicesByLeg,
}) => {
  const getDateKey = (value?: string) => {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString().slice(0, 10);
  };

  return (
  <div className="space-y-5">
    {/* Section Header */}
    <div className="flex items-center justify-between">
      <div>
        <span className="text-[10px] font-bold text-slate-900 uppercase tracking-[0.15em]">Flight Sectors</span>
        <p className="text-xs text-slate-500 mt-0.5">
          {legs.length} leg{legs.length !== 1 ? 's' : ''} configured
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onAutoReturn}
          className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-teal-700 bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-300/60 rounded-lg hover:border-teal-400 hover:shadow-sm transition-all"
        >
          <RotateCcw className="w-4 h-4" />
          Auto Return
        </button>
        <button
          type="button"
          onClick={onAddLeg}
          className="btn-premium btn-glow flex items-center gap-1.5 px-4 py-2.5 rounded-lg"
        >
          <Plus className="w-4 h-4" />
          Add Leg
        </button>
      </div>
    </div>

    {/* Leg Cards */}
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <SectionAdvisories title="Route Guard" section="ROUTE" advisories={advisories.filter(item => !item.legId)} compact />
      <SectionAdvisories title="Timing Guard" section="TIMING" advisories={advisories.filter(item => !item.legId)} compact />
    </div>

    <div className="space-y-4">
      {legs.map((leg, index) => {
        const originMetar = weatherByCode[String(leg.from || '').toUpperCase()];
        const destinationMetar = weatherByCode[String(leg.to || '').toUpperCase()];
        const nextLeg = legs[index + 1];
        const hasSleepOverCarry = Number(sleepOverChargeByLeg[leg.id] || 0) > 0 && !!nextLeg;
        const carriesFromSameOutstation =
          hasSleepOverCarry &&
          String(leg.to || '').trim().toUpperCase() === String(nextLeg?.from || '').trim().toUpperCase();
        const overnightDateShift =
          hasSleepOverCarry &&
          getDateKey(nextLeg?.etd) &&
          getDateKey(leg.etd) &&
          getDateKey(nextLeg?.etd) !== getDateKey(leg.etd);
        return (
          <div key={leg.id} className="space-y-3">
            <QuoteLegCard
              leg={leg}
              index={index}
              airstrips={airstrips}
              issueDate={issueDate}
              advisories={advisories.filter(item => item.legId === leg.id)}
              originMetar={originMetar}
              destinationMetar={destinationMetar}
              onRemove={onRemoveLeg}
              onUpdate={onUpdateLeg}
              resolvedExtraServices={resolvedExtraServicesByLeg[leg.id] || []}
              sleepOverCharge={sleepOverChargeByLeg[leg.id] || 0}
            />
            {hasSleepOverCarry && (
              <div className="mx-5 rounded-[1.35rem] border border-violet-200/80 bg-gradient-to-r from-violet-50 via-indigo-50 to-sky-50 px-4 py-3 shadow-[0_10px_24px_rgba(99,102,241,0.08)]">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-2xl border border-violet-200 bg-white/80 text-violet-700 shadow-sm">
                    <MoonStar className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-violet-700">
                      <span>Overnight Carry</span>
                      {overnightDateShift && <ArrowDown className="h-3.5 w-3.5" />}
                    </div>
                    <p className="mt-1 text-sm font-semibold text-slate-800">
                      {carriesFromSameOutstation
                        ? `Aircraft holds overnight at ${leg.to} and starts the next mission from the same outstation.`
                        : `Aircraft holds overnight at ${leg.to} before next-day reposition to ${nextLeg?.from || 'the next pickup point'}.`}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      {carriesFromSameOutstation
                        ? 'No duplicate dead leg is added because the next passenger leg already moves the aircraft.'
                        : 'A new dead leg is only required if the next mission starts from a different origin.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>

    {/* Empty State */}
    {legs.length === 0 && (
      <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-slate-300 rounded-3xl bg-gradient-to-br from-slate-50/80 to-indigo-50/40 shadow-sm">
        <div className="text-center space-y-3">
          <p className="text-lg font-semibold text-slate-700">No flight sectors configured</p>
          <p className="text-sm text-slate-500">Create your first leg to get started</p>
        </div>
        <button
          type="button"
          onClick={onAddLeg}
          className="btn-premium btn-glow mt-5 flex items-center gap-1.5 px-5 py-3 rounded-lg text-sm"
        >
          <Plus className="w-4 h-4" />
          Create First Leg
        </button>
      </div>
    )}
  </div>
  );
};

export default QuoteLegs;
